"use client"

import { useEffect, useState } from "react"
import { Search as SearchIcon, Minus, Plus, Download, ChevronUp, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { useEditorStore } from "@/lib/editor-store"
import { usePdfViewerState, patchPdfViewerState } from "@/lib/pdf-viewer-state-store"
import { useLayoutPanels } from "@/components/editor/dock/layout-context"


export function PdfRibbonToolbar() {
  const { postToFlutterFrame } = useEditorStore()
  const { activeTabId } = useLayoutPanels()
  const pdfState = usePdfViewerState()
  const [pageDraft, setPageDraft] = useState<string | null>(null)

  const page = pdfState?.page ?? 1
  const pageCount = pdfState?.pageCount ?? 0
  const zoomPercent = pdfState?.zoomPercent ?? 100
  const url = pdfState?.url ?? null
  // Stored per-tab (not local state here) so switching to a different PDF
  // tab shows *that* tab's query instead of whatever was last typed.
  const searchQuery = pdfState?.searchQuery ?? ""

  // Re-run the active search whenever the PDF URL updates (after compilation)
  useEffect(() => {
    if (url && searchQuery.trim()) {
      // Give the Flutter viewer a brief tick to finish document mount
      const timer = setTimeout(() => {
        postToFlutterFrame({ type: "PDF_SEARCH", query: searchQuery.trim() })
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [url])

  const commitPageDraft = () => {
    if (pageDraft === null) return
    const parsed = parseInt(pageDraft, 10)
    if (Number.isFinite(parsed)) {
      postToFlutterFrame({ type: "PDF_GOTO_PAGE", page: parsed })
    }
    setPageDraft(null)
  }

  const runSearch = (query: string) => {
    if (activeTabId) patchPdfViewerState(activeTabId, { searchQuery: query })
    postToFlutterFrame({ type: "PDF_SEARCH", query })
  }

  const matchLabel =
      searchQuery && pdfState?.searchMatchCount != null
          ? pdfState.searchMatchCount === 0
              ? "0/0"
              : `${pdfState.searchCurrentIndex ?? 1}/${pdfState.searchMatchCount}`
          : null

  return (
    <div
      className={cn(
        "flex h-8 shrink-0 select-none items-center gap-2 px-3",
        "bg-[var(--ed-ribbon-bg)]",
        "border-b border-[var(--ed-ribbon-border)]",
        "text-xs text-[var(--ed-ribbon-text)]",
      )}
    >
      {/* Page indicator */}
      <div className="flex h-[22px] items-center gap-1 rounded-[4px] border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)] px-1.5">
        <input
          value={pageDraft ?? String(page)}
          onFocus={() => setPageDraft(String(page))}
          onChange={(e) => setPageDraft(e.target.value.replace(/[^\d]/g, ""))}
          onBlur={commitPageDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur()
            else if (e.key === "Escape") setPageDraft(null)
          }}
          disabled={!url}
          className="w-6 bg-transparent text-center outline-none disabled:opacity-50"
          aria-label="Page number"
        />
        <span className="text-[#9CA3AF]">/ {pageCount || "–"}</span>
      </div>

      {/* Zoom controls */}
      <div className="flex h-[22px] items-center overflow-hidden rounded-[4px] border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)]">
        <button
          type="button"
          disabled={!url}
          onClick={() => postToFlutterFrame({ type: "PDF_ZOOM_OUT" })}
          className="flex h-full w-[22px] items-center justify-center hover:bg-[var(--ed-ribbon-icon-hover)] disabled:opacity-40"
          aria-label="Zoom out"
        >
          <Minus className="h-3 w-3" />
        </button>
        <div className="h-3.5 w-px bg-[var(--ed-ribbon-field-border)]" aria-hidden />
        <span className="w-11 text-center tabular-nums">{Math.round(zoomPercent)}%</span>
        <div className="h-3.5 w-px bg-[var(--ed-ribbon-field-border)]" aria-hidden />
        <button
          type="button"
          disabled={!url}
          onClick={() => postToFlutterFrame({ type: "PDF_ZOOM_IN" })}
          className="flex h-full w-[22px] items-center justify-center hover:bg-[var(--ed-ribbon-icon-hover)] disabled:opacity-40"
          aria-label="Zoom in"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>

      {/* Search */}
      <div className="relative w-[180px]">
        <input
          value={searchQuery}
          onChange={(e) => runSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              postToFlutterFrame({ type: e.shiftKey ? "PDF_SEARCH_PREV" : "PDF_SEARCH_NEXT" })
            } else if (e.key === "ArrowDown") {
              e.preventDefault()
              postToFlutterFrame({ type: "PDF_SEARCH_NEXT" })
            } else if (e.key === "ArrowUp") {
              e.preventDefault()
              postToFlutterFrame({ type: "PDF_SEARCH_PREV" })
            } else if (e.key === "Escape") {
              e.preventDefault()
              runSearch("")
            }
          }}
          disabled={!url}
          placeholder="Search"
          aria-label="Search in PDF"
          className={cn(
            "h-[22px] w-full rounded-[4px] border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)] pl-6 text-xs outline-none disabled:opacity-50",
            matchLabel ? "pr-9" : "pr-2",
            "placeholder:text-[#9CA3AF]",
            "focus-visible:ring-1 focus-visible:ring-[#5B8CFF]",
          )}
        />
        <SearchIcon className="pointer-events-none absolute left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[#6B7280]" />
        {matchLabel && (
          <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#9CA3AF] tabular-nums">
            {matchLabel}
          </span>
        )}
      </div>

      {/* Step through search matches */}
      <div className="flex h-[22px] items-center overflow-hidden rounded-[4px] border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)]">
        <button
          type="button"
          disabled={!url || !searchQuery || !pdfState?.searchMatchCount}
          onClick={() => postToFlutterFrame({ type: "PDF_SEARCH_PREV" })}
          className="flex h-full w-[20px] items-center justify-center hover:bg-[var(--ed-ribbon-icon-hover)] disabled:opacity-40"
          aria-label="Previous match"
          title="Previous match"
        >
          <ChevronUp className="h-3 w-3" />
        </button>
        <div className="h-3.5 w-px bg-[var(--ed-ribbon-field-border)]" aria-hidden />
        <button
          type="button"
          disabled={!url || !searchQuery || !pdfState?.searchMatchCount}
          onClick={() => postToFlutterFrame({ type: "PDF_SEARCH_NEXT" })}
          className="flex h-full w-[20px] items-center justify-center hover:bg-[var(--ed-ribbon-icon-hover)] disabled:opacity-40"
          aria-label="Next match"
          title="Next match"
        >
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>

      {/* Download the PDF currently loaded in the viewer */}
      <a
        href={url ?? undefined}
        download
        aria-disabled={!url}
        title="Download PDF"
        aria-label="Download PDF"
        className={cn(
          "flex h-[22px] w-[22px] items-center justify-center rounded-[4px]",
          url ? "hover:bg-[var(--ed-ribbon-download-hover)]" : "pointer-events-none opacity-40",
        )}
      >
        <Download className="h-3.5 w-3.5" />
      </a>
    </div>
  )
}
